import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";

const POLAROID_1 =
  "https://images.pexels.com/photos/13327132/pexels-photo-13327132.jpeg";
const POLAROID_2 =
  "https://images.unsplash.com/photo-1673767298248-b128f17f89af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxnZW4lMjB6JTIwY29udGVudCUyMGNyZWF0b3J8ZW58MHx8fHwxNzgxNjQxMTM5fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { content } = useSiteContent();
  const h = content.hero;
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="band bg-[#FF5B17] text-[#0A0A0A] pt-24 lg:pt-28 pb-14"
    >
      {/* corner crosses */}
      <div className="absolute top-20 left-3 font-display text-3xl text-[#0A0A0A]/80">+</div>
      <div className="absolute top-20 right-3 font-display text-3xl text-[#0A0A0A]/80">+</div>

      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 relative">
        {/* Sticker row */}
        <div className="flex justify-end mb-4">
          <div className="wiggle inline-block" style={{ "--r": "-6deg" }}>
            <div className="sticker dark" data-testid="hero-sticker">
              <Sparkles className="w-3.5 h-3.5" /> RTAMA · CHAPTER I · 2026
            </div>
          </div>
        </div>

        {/* Tiny meta */}
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] mb-8">
          <span data-testid="hero-meta-left">001 / Cover</span>
          <span className="hidden sm:inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] inline-block" />
            Limassol, Cyprus → Worldwide
          </span>
          <span>An independent influencer studio</span>
        </div>

        {/* MASSIVE headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-title"
          className="font-display text-[#0A0A0A] mt-2"
          style={{
            fontSize: "clamp(4rem, 17vw, 19rem)",
            lineHeight: 0.78,
            letterSpacing: "-0.025em",
          }}
        >
          <span className="block">We turn</span>
          <span className="block">
            <span className="font-hand text-[#FAF5E8] mr-3 -ml-1" style={{ fontSize: "0.42em", letterSpacing: 0 }}>
              followers
            </span>
            <span className="text-outline">FOLLOWERS</span>
          </span>
          <span className="block">into <span className="font-serif-it text-[#FAF5E8]" style={{ fontStyle: "italic", letterSpacing: "-0.04em" }}>fortunes.</span></span>
        </motion.h1>

        {/* Handwritten note + arrow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="absolute right-6 sm:right-16 lg:right-32 hidden lg:flex items-center gap-3 z-10"
          style={{ top: "32%", transform: "rotate(6deg)" }}
        >
          <span className="font-hand text-3xl text-[#0A0A0A]">{h.handwritten_note}</span>
        </motion.div>

        {/* Lower row: stat + cta + polaroids */}
        <div className="grid grid-cols-12 gap-6 mt-12 lg:mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="col-span-12 lg:col-span-5"
          >
            <p className="font-mono text-sm sm:text-base text-[#0A0A0A] leading-relaxed max-w-md">
              {h.deck}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("contact")}
                className="btn-chunky"
              >
                Get represented
                <ArrowDownRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("services")}
                className="btn-outline"
              >
                See services
              </button>
            </div>

            <div className="mt-9 grid grid-cols-3 gap-3 max-w-md border-t-2 border-[#0A0A0A] pt-5">
              <div>
                <div className="font-display text-4xl">{h.stat_1_value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1">{h.stat_1_label}</div>
              </div>
              <div>
                <div className="font-display text-4xl">{h.stat_2_value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1">{h.stat_2_label}</div>
              </div>
              <div>
                <div className="font-display text-4xl">{h.stat_3_value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1">{h.stat_3_label}</div>
              </div>
            </div>
          </motion.div>

          {/* Polaroid stack */}
          <div className="col-span-12 lg:col-span-7 relative h-[360px] sm:h-[420px]">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 0 }}
              animate={{ opacity: 1, y: 0, rotate: -7 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="absolute top-0 left-4 sm:left-12 polaroid w-[210px] sm:w-[260px]"
              data-testid="hero-polaroid-1"
            >
              <div className="relative overflow-hidden h-[260px] sm:h-[320px]">
                <img
                  src={POLAROID_1}
                  alt="Creator portrait"
                  className="w-full h-full object-cover"
                />
                <div className="tape" style={{ top: "-14px", left: "30%", transform: "rotate(-6deg)" }} />
              </div>
              <div className="font-hand text-xl mt-2">{h.polaroid_1_caption}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50, rotate: 0 }}
              animate={{ opacity: 1, y: 0, rotate: 9 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="absolute top-8 right-6 sm:right-12 polaroid w-[200px] sm:w-[240px]"
              data-testid="hero-polaroid-2"
            >
              <div className="relative overflow-hidden h-[240px] sm:h-[300px]">
                <img
                  src={POLAROID_2}
                  alt="Gen Z creator"
                  className="w-full h-full object-cover"
                />
                <div className="tape" style={{ top: "-14px", right: "20%", transform: "rotate(7deg)" }} />
              </div>
              <div className="font-hand text-xl mt-2 text-[#FF2D9C]">{h.polaroid_2_caption}</div>
            </motion.div>

            {/* Starburst */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="absolute bottom-2 left-1/2 -translate-x-1/2 lg:left-[40%] flex items-center justify-center"
              data-testid="hero-starburst"
            >
              <div className="starburst slow-spin" />
              <div className="absolute inset-0 flex items-center justify-center text-center">
                <div className="font-display leading-[0.8] text-[#0A0A0A]">
                  <div className="text-[10px] tracking-[0.2em] font-mono mb-1">EST.</div>
                  <div className="text-3xl">{h.starburst_year}</div>
                  <div className="text-[10px] tracking-[0.2em] font-mono mt-1">CYPRUS</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="mt-12 border-y-2 border-[#0A0A0A] bg-[#0A0A0A] overflow-hidden">
        <div className="loop py-2.5">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {[
                "GET REPRESENTED",
                "ANY PLATFORM",
                "ANY TIMEZONE",
                "ANY ALGORITHM",
                "CYPRUS ↔ WORLDWIDE",
                "CREATORS FIRST",
              ].map((t, i) => (
                <span
                  key={`${dup}-${i}`}
                  className="font-display text-3xl sm:text-4xl text-[#FF5B17] px-6 flex items-center gap-6"
                >
                  {t}
                  <span className="text-[#FAF5E8]">✶</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
